import { popoverSurface } from "@ui/components/PopoverSurface";
import { OverlayArrow, Popover as RACPopover, type PopoverProps as RACPopoverProps } from "@ui/primitives/Popover";
import { cx } from "@ui/utils/cx";
import { type CSSProperties, type MouseEvent, type ReactNode, useCallback, useEffect, useRef, useState } from "react";

const OPEN_DELAY = 300;
const CLOSE_DELAY = 200;

export interface HoverCardProps {
  children: ReactNode;
  className?: string;
  content: ReactNode;
  offset?: number;
  placement?: RACPopoverProps["placement"];
  triggerClassName?: string;
  /** Render a small caret connecting the card to its trigger. Default false. */
  showArrow?: boolean;
  /** Card surface + caret fill. Default white; pass a paper tone to re-theme. */
  surfaceColor?: string;
  /** Keep the card at least this many px from the viewport edge on collision. */
  containerPadding?: number;
}

export function HoverCard({
  children,
  className,
  content,
  offset = 8,
  placement = "top",
  triggerClassName,
  showArrow = false,
  surfaceColor = "#ffffff",
  containerPadding = 12,
}: HoverCardProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const openTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);
  const closeTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    return () => {
      clearTimeout(openTimeout.current);
      clearTimeout(closeTimeout.current);
    };
  }, []);

  const open = useCallback(() => {
    clearTimeout(closeTimeout.current);
    openTimeout.current = setTimeout(() => setIsOpen(true), OPEN_DELAY);
  }, []);

  const close = useCallback(() => {
    clearTimeout(openTimeout.current);
    closeTimeout.current = setTimeout(() => setIsOpen(false), CLOSE_DELAY);
  }, []);

  const cancelClose = useCallback(() => {
    clearTimeout(closeTimeout.current);
  }, []);

  // Touch/pen/keyboard activation toggles the card open (there is no hover on
  // those inputs); a mouse click only ensures it is open so it never fights the
  // hover-open/close timers. `pointerType` is "" for keyboard (Enter/Space).
  const handleClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    clearTimeout(openTimeout.current);
    clearTimeout(closeTimeout.current);
    if ((event.nativeEvent as PointerEvent).pointerType === "mouse") {
      setIsOpen(true);
    } else {
      setIsOpen((prev) => !prev);
    }
  }, []);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        className={cx(
          "appearance-none border-none bg-transparent p-0 text-left font-[inherit] text-[length:inherit] text-inherit",
          triggerClassName,
        )}
        onMouseEnter={open}
        onMouseLeave={close}
        onFocus={open}
        onBlur={close}
        onClick={handleClick}
      >
        {children}
      </button>
      <RACPopover
        triggerRef={triggerRef}
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        isNonModal
        placement={placement}
        offset={offset}
        containerPadding={containerPadding}
        style={{ "--hc-surface": surfaceColor } as CSSProperties}
        // The shared popover card chrome, retinted via the surface variable.
        className={popoverSurface({ className: cx("bg-[var(--hc-surface)] p-5", className) })}
      >
        {showArrow && (
          // A caret tying the card to its trigger. RAC sets `data-placement`; we
          // rotate the down-pointing triangle to match. Filled with the card surface
          // and carrying the card's own hairline (stroked on the two outer legs only,
          // never the base that joins the card) + soft shadow, so it reads as an
          // extension of the card rather than a bare flap.
          <OverlayArrow className="group">
            <svg
              width={16}
              height={8}
              viewBox="0 0 16 8"
              aria-hidden="true"
              className="-mt-px block fill-[var(--hc-surface)] [filter:drop-shadow(0_2px_1.5px_rgba(0,0,0,0.08))] group-data-[placement=bottom]:rotate-180 group-data-[placement=left]:-rotate-90 group-data-[placement=right]:rotate-90"
            >
              <path d="M0 0 H16 L8 8 Z" />
              <path
                d="M0 0 L8 8 L16 0"
                fill="none"
                stroke="rgba(0,0,0,0.05)"
                strokeWidth={1}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            </svg>
          </OverlayArrow>
        )}
        {/* biome-ignore lint/a11y/noStaticElementInteractions: hover detection for popover persistence */}
        <div onMouseEnter={cancelClose} onMouseLeave={close}>
          {content}
        </div>
      </RACPopover>
    </>
  );
}
